import { useState, useCallback, useEffect, useRef } from 'react';
import { css } from '@emotion/react';
import {
  AuditEvent,
  ChangeTypeUpdateHoldMusic,
  ObjectTypeHoldMusic,
  UpdateHoldMusicContext,
} from '@weave/schema-gen-ts/dist/apis/api/phone/audits/v1/api.pb';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { buildAuditFilter, useSearchAuditEvents } from '@frontend/api-phone-audits';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { createISODateInUTC, formatDate } from '@frontend/date';
import appConfig from '@frontend/env';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { usePhoneSettingsShallowStore } from '@frontend/phone-config';
import { validateUUIDV4 } from '@frontend/string';
import { Text, useAlert, useDebouncedValue, useModalControl } from '@frontend/design-system';
import {
  DetailsHeader,
  LocationDetails,
} from '../../components/phone-system-history/history-details-tray/details-header';
import {
  HISTORY_TRAY_WIDTH,
  HistoryDetailsTray,
  useHistoryTrayShouldOverlap,
} from '../../components/phone-system-history/history-details-tray/history-details-tray';
import {
  SETTING_TYPE_OPTIONS,
  ACTOR_SYSTEM_OPTION,
  ACTOR_WEAVE_OPTION,
} from '../../components/phone-system-history/phone-system-history-config';
import {
  AuditEventFilters,
  defaultAuditEventFilters,
} from '../../components/phone-system-history/phone-system-history-filters';
import { PhoneSystemHistoryTable } from '../../components/phone-system-history/phone-system-history-table';
import { PhoneSettingsPage } from '../../components/settings/settings-page';
import { logError } from '../../utils/loggers';
import { useTenantLocationIds } from '../../utils/use-tenant-location-ids';

export function PhoneSystemHistoryIndex() {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const tenantId = settingsTenantLocation?.phoneTenantId || '';
  const tenantLocationIds = useTenantLocationIds();

  const alerts = useAlert();

  const isUnifyType = settingsTenantLocation?.locationType === 'unify';

  const canEditUsers = tenantLocationIds.some((locationId) => hasSchemaACL(locationId, Permission.USER_WRITE));

  const { t } = useTranslation('phone');

  const detailsTray = useModalControl();
  const isTrayOpen = detailsTray.modalProps.show;
  const shouldOverlapDetailsTray = useHistoryTrayShouldOverlap();
  // When the tray overlaps (small screens) the page content stays full-width instead of being squished next to it.
  const isSquishedMode = isTrayOpen && !shouldOverlapDetailsTray;

  const [selectedEvent, setSelectedEvent] = useState<AuditEvent | undefined>(undefined);

  // ----- Filter state -----
  const [filters, setFilters] = useState<AuditEventFilters>(defaultAuditEventFilters);
  const [searchValue, setSearchValue] = useState('');
  const debouncedSearch = useDebouncedValue(searchValue, 500);

  // ----- Pagination state -----
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  // Store each page's nextPageToken in an array so that we can navigate back and forth between pages without losing
  // the token for each page. Index 0 = page 1 (undefined), index 1 = page 2 token, etc.
  const pageTokensRef = useRef<(string | undefined)[]>([undefined]);

  // ----- Cutoff time state -----
  // Captured once when the page loads to establish an upper bound for audit event timestamps. This ensures that
  // the table and tray never show events newer than each other, maintaining data consistency. The cutoff is frozen
  // at page-load time and persists across table pagination and filter changes. When the user sets an endDate filter,
  // the older of the two (cutoff time vs. endDate) is used to prevent showing events outside the user's requested range.
  // Using lazy initialization to set the value once on mount, avoiding an extra render cycle and duplicate query call.
  const [historyCutoffTime] = useState<string>(() => new Date().toISOString());

  const resetPagination = useCallback(() => {
    setPageNumber(1);
    pageTokensRef.current = [undefined];
  }, []);

  const handleFiltersChange = useCallback(
    (nextFilters: AuditEventFilters) => {
      setFilters(nextFilters);
      resetPagination();
    },
    [resetPagination]
  );

  // Reset pagination when the debounced search value changes.
  const prevDebouncedSearchRef = useRef(debouncedSearch);
  useEffect(() => {
    if (prevDebouncedSearchRef.current !== debouncedSearch) {
      prevDebouncedSearchRef.current = debouncedSearch;
      resetPagination();
    }
  }, [debouncedSearch, resetPagination]);

  const { data, isLoading } = useSearchAuditEvents(
    {
      tenantId,
      filter: buildAuditFilter(getFilterParams(filters, debouncedSearch, historyCutoffTime)) || undefined,
      pageSize,
      pageToken: pageTokensRef.current[pageNumber - 1],
    },
    { baseUrl: appConfig.BACKEND_API_V2 },
    {
      retry: false,
      enabled: !!tenantId,
      // Keep cached results for the lifetime of the page (so re-selecting the same filters doesn't refetch), but
      // force a fresh fetch every time this component mounts (i.e. navigating away from and back to the page).
      staleTime: Infinity,
      refetchOnMount: 'always',
      keepPreviousData: true,
      onSuccess(responseData) {
        // Store the nextPageToken so we can navigate to the next page later.
        if (responseData.nextPageToken) {
          pageTokensRef.current[pageNumber] = responseData.nextPageToken;
        }
      },
      onError(error) {
        alerts.error({
          message: t('Failed to fetch phone system history'),
        });
        logError('PhoneSystemHistoryIndex:useSearchAuditEvents', {
          data: {
            tenantId,
            search: debouncedSearch,
            objectTypes: filters.objectTypes,
            actions: filters.actions,
            userFilters: filters.userIds,
            startDate: filters.startDate,
            endDate: filters.endDate,
            error,
          },
        });
      },
    }
  );

  const handlePageChange = useCallback((direction: 'next' | 'prev') => {
    if (direction === 'next') {
      setPageNumber((prev) => prev + 1);
    } else {
      setPageNumber((prev) => Math.max(1, prev - 1));
    }
  }, []);

  const handlePageSizeChange = useCallback(
    (newSize: number) => {
      setPageSize(newSize);
      resetPagination();
    },
    [resetPagination]
  );

  // Captured at tray-open time so that navigating between table pages (which updates dataUpdatedAt) does not
  // destabilize the tray's query key and trigger unnecessary refetches. The value is frozen when the tray opens and
  // only refreshes the next time the tray is opened.

  return (
    <>
      <PhoneSettingsPage
        renderContainer={false}
        title={t('Phone System History')}
        subtitle={
          <span css={subtitleStyle}>
            {data?.trackingStartTime && (
              <Text size='medium' color='light'>
                {t('Data available starting {{date}}', {
                  date: formatDate(data.trackingStartTime, 'MMMM DD, YYYY h:mm A'),
                })}
              </Text>
            )}
            <Text>
              {t(
                'View a history of phone system changes, including what changed, who made the change, and when it occurred.'
              )}
            </Text>
          </span>
        }
        breadcrumbs={[
          { label: t('Phone'), to: '/phone/main' },
          { label: t('Phone System History'), to: '/phone/phone-system-history' },
        ]}
        css={settingsPageStyle(isSquishedMode)}
      >
        <div css={pageContentStyle}>
          <PhoneSystemHistoryAvailabilityBanner />

          <PhoneSystemHistoryTable
            events={data?.auditEvents ?? []}
            isLoading={isLoading}
            canEditUsers={canEditUsers}
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onDetailsClick={(_event) => {
              setSelectedEvent(_event);
              detailsTray.openModal();
            }}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            isCompact={isTrayOpen}
            selectedEventId={selectedEvent?.id}
            pageNumber={pageNumber}
            pageSize={pageSize}
            hasNextPage={!!data?.nextPageToken}
            hasPreviousPage={pageNumber > 1}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      </PhoneSettingsPage>

      <HistoryDetailsTray
        modalControls={detailsTray}
        tenantId={settingsTenantLocation?.phoneTenantId ?? ''}
        callObjectId={selectedEvent?.objectId}
        callObjectType={selectedEvent?.objectType}
        auditEventId={selectedEvent?.id}
        refetchOnMount={true}
        historyCutoffTime={historyCutoffTime}
        showAllHistoryButton={false}
        HeaderComponent={
          <DetailsHeader
            objectType={selectedEvent?.objectType}
            onClose={() => {
              detailsTray.closeModal();
              setSelectedEvent(undefined);
            }}
          >
            {isUnifyType &&
              selectedEvent?.objectType === ObjectTypeHoldMusic &&
              selectedEvent?.changes?.[0]?.type === ChangeTypeUpdateHoldMusic && (
                <LocationDetails
                  locationNames={[
                    (selectedEvent.changes?.[0] as { updateHoldMusic: UpdateHoldMusicContext })?.updateHoldMusic
                      ?.locations?.[0]?.locationName ?? t('Unknown Location'),
                  ]}
                />
              )}
          </DetailsHeader>
        }
      />
    </>
  );
}

function PhoneSystemHistoryAvailabilityBanner() {
  const { t } = useTranslation('phone');

  return (
    <div css={availabilityBannerStyle}>
      <div css={iconWrapperStyle}>
        <Icon name='history-small' color='subdued' css={iconStyle} />
      </div>
      <div>
        <Text weight='bold'>{t('Currently Available:')}</Text>
        <Text size='medium' color='light'>
          {SETTING_TYPE_OPTIONS.map(({ label }) => label).join(', ')}
        </Text>
      </div>
    </div>
  );
}

/**
 * Returns the filter params to pass to the backend based on the current filter state and search input.
 * If the search input is a UUID, it is treated as an object ID lookup instead of a name search.
 *
 * @param filters The current filter state.
 * @param searchInput The current search input value.
 * @param historyCutoffTime The page-level cutoff time to cap audit event timestamps. Ensures data consistency
 *   between the table and tray by preventing either from showing events newer than this time. When the user sets
 *   an endDate filter, the older (earlier) of the two timestamps is used.
 * @returns An object containing the filter params to pass to the backend.
 */
const getFilterParams = (filters: AuditEventFilters, searchInput: string, historyCutoffTime: string) => {
  // If the search value is a UUID, treat it as an object ID lookup instead of a name search.
  const isSearchUUID = !!searchInput && validateUUIDV4(searchInput);

  // Determine the effective endTime for the filter. If an endDate is specified, we need to ensure it doesn't
  // allow events newer than the historyCutoffTime. We use the older (earlier) of the two timestamps.
  let endTime: string;
  if (filters.endDate) {
    const endDateISO = createISODateInUTC(filters.endDate).replace('T00:00:00.000Z', 'T23:59:59.999Z');
    // Use the earlier (older) timestamp to keep events capped at both the user's filter and the cutoff.
    endTime = endDateISO < historyCutoffTime ? endDateISO : historyCutoffTime;
  } else {
    // No user-specified endDate, so use the cutoff time to cap the results.
    endTime = historyCutoffTime;
  }

  return {
    objectTypes: filters.objectTypes.length ? filters.objectTypes : undefined,
    actions: filters.actions.length ? filters.actions : undefined,
    actorTypes: (() => {
      // If the user has selected either the "System" or "Weave" actor types, we want to include those in the
      // actorTypes filter. Otherwise, we return undefined so the backend doesn't filter by actor type.
      const selected = filters.userIds.filter((id) => id === ACTOR_SYSTEM_OPTION || id === ACTOR_WEAVE_OPTION);
      return selected.length ? selected : undefined;
    })(),
    actorIds: (() => {
      // Filter out the "System" and "Weave" actor types from the userIds filter, since those are handled by
      // the actorTypes field.
      const realUserIds = filters.userIds.filter((id) => id !== ACTOR_SYSTEM_OPTION && id !== ACTOR_WEAVE_OPTION);
      return realUserIds.length ? realUserIds : undefined;
    })(),
    // If the search value is a UUID, treat it as an object ID lookup instead of a name search.
    objectId: isSearchUUID ? searchInput : undefined,
    searchInput: searchInput && !isSearchUUID ? searchInput : undefined,
    startTime: filters.startDate ? createISODateInUTC(filters.startDate) : undefined,
    endTime,
  };
};

// --- Styles ---

const settingsPageStyle = (isSquished: boolean) => css`
  header {
    div:first-of-type {
      gap: 0;
    }
  }
  // subtract the width of the tray from the width of the page when the tray is squishing the content, so that the page
  // content doesn't get hidden behind it
  width: ${isSquished ? `calc(100% - ${HISTORY_TRAY_WIDTH}px)` : '100%'};
  transition: width 0.3s ease;
`;

const subtitleStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-xs);
`;

const pageContentStyle = css`
  display: flex;
  flex-direction: column;
  gap: var(--fabric-space-gap-lg);
`;

const availabilityBannerStyle = css`
  display: flex;
  align-items: center;
  gap: var(--fabric-space-gap-sm);
  padding: var(--fabric-space-gap-sm) var(--fabric-space-gap-lg);
  border: 1px solid var(--fabric-color-border-default);
  border-radius: var(--fabric-radius-md);
  background-color: var(--fabric-color-surface-inset-default);
  max-width: 700px;
`;

const iconWrapperStyle = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: var(--fabric-color-surface-inset-hover);
`;

const iconStyle = css`
  flex-shrink: 0;
  width: 24px;
`;
